import { api } from '../services/api';

const PREFIX = '/admin/mcquiz';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CreateTestData {
  title: string;
  slug: string;
  description?: string;
  test_type: 'full_mock' | 'sectional' | 'topic_wise' | 'daily_challenge';
  timer_minutes?: number;
  total_marks?: number;
  marks_per_question?: number;
  negative_ratio?: number;
  access_level: 'free' | 'premium';
  category?: string;
}

export interface SearchQuestionsParams {
  subject?: string;
  difficulty?: string;
  bank_id?: number;
  q?: string;
  limit?: number;
  offset?: number;
}

// ---------------------------------------------------------------------------
// Question Banks
// ---------------------------------------------------------------------------

export const uploadQuestionBank = (file: File, title: string, description?: string) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('title', title);
  if (description) formData.append('description', description);
  return api.post(`${PREFIX}/banks/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const listBanks = () => api.get(`${PREFIX}/banks`);

export const getBank = (bankId: number) => api.get(`${PREFIX}/banks/${bankId}`);

export const deleteBank = (bankId: number) => api.delete(`${PREFIX}/banks/${bankId}`);

// ---------------------------------------------------------------------------
// Questions
// ---------------------------------------------------------------------------

export const searchQuestions = (params: SearchQuestionsParams) =>
  api.get(`${PREFIX}/questions`, { params });

export const editQuestion = (questionId: number, data: any) =>
  api.patch(`${PREFIX}/questions/${questionId}`, data);

// ---------------------------------------------------------------------------
// Tests
// ---------------------------------------------------------------------------

export const createTest = (data: CreateTestData) =>
  api.post(`${PREFIX}/tests`, data);

export const listTests = (status?: string) =>
  api.get(`${PREFIX}/tests`, { params: status ? { status } : undefined });

export const getTest = (testId: number) =>
  api.get(`${PREFIX}/tests/${testId}`);

export const updateTest = (testId: number, data: any) =>
  api.patch(`${PREFIX}/tests/${testId}`, data);

export const deleteTest = (testId: number) =>
  api.delete(`${PREFIX}/tests/${testId}`);

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

export const addSection = (testId: number, data: any) =>
  api.post(`${PREFIX}/tests/${testId}/sections`, data);

export const updateSection = (testId: number, sectionId: number, data: any) =>
  api.patch(`${PREFIX}/tests/${testId}/sections/${sectionId}`, data);

export const deleteSection = (testId: number, sectionId: number) =>
  api.delete(`${PREFIX}/tests/${testId}/sections/${sectionId}`);

// ---------------------------------------------------------------------------
// Section Questions
// ---------------------------------------------------------------------------

export const assignQuestions = (
  testId: number,
  sectionId: number,
  data: { question_ids: number[]; marks_per_question: number },
) => api.post(`${PREFIX}/tests/${testId}/sections/${sectionId}/questions`, data);

export const removeQuestion = (testId: number, sectionId: number, questionId: number) =>
  api.delete(`${PREFIX}/tests/${testId}/sections/${sectionId}/questions/${questionId}`);

// ---------------------------------------------------------------------------
// Auto-populate & lifecycle
// ---------------------------------------------------------------------------

export const autoPopulate = (testId: number, data: any) =>
  api.post(`${PREFIX}/tests/${testId}/auto-populate`, data);

export const moveToPreview = (testId: number) =>
  api.post(`${PREFIX}/tests/${testId}/preview`);

export const publishTest = (testId: number) =>
  api.post(`${PREFIX}/tests/${testId}/publish`);

export const archiveTest = (testId: number) =>
  api.post(`${PREFIX}/tests/${testId}/archive`);

// ---------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------

export const getTestAnalytics = (testId: number) =>
  api.get(`${PREFIX}/tests/${testId}/analytics`);

export const getOverviewAnalytics = () =>
  api.get(`${PREFIX}/analytics/overview`);
